/* press-logo-tabbed-slider — logos are the navigation; quotes FADE, never slide.
 *
 * The markup is complete without this file: the tabs are real <button role=tab>
 * elements, the quote panels are stacked in one grid cell with the first one
 * visible (`is-active`) and the rest `inert`. This script adds:
 *
 *   1. TAB → QUOTE: clicking a logo makes its quote the active (faded-in) panel.
 *   2. GESTURE → QUOTE: a horizontal drag (mouse) or swipe (touch / pen) of more
 *      than SWIPE_PX steps one quote in the gesture's direction. The panels never
 *      move with the pointer — the reader only ever sees a fade — and a click that
 *      follows a real drag is swallowed so a link under the cursor does not fire.
 *      `touch-action: pan-y` on the stage keeps vertical page scroll native.
 *   3. AUTOPLAY: advances every `data-interval` ms (default 3000), wrapping.
 *      Paused on hover, focus-within, pointer down, while the tab is hidden,
 *      while the section is off-screen, and entirely under reduced motion.
 *      Any user interaction restarts the timer so the next auto-advance never
 *      lands right after a manual one.
 *   4. KEYBOARD: Left/Right/Home/End on the tablist, per the WAI-ARIA tabs
 *      pattern; the inactive panels are `inert` so Tab skips their links.
 *   5. The active logo is kept in view when the logo row itself overflows.
 *
 * Guarded define: two instances on one page load this file twice
 * (section-level script tags do not compose), and a second
 * customElements.define throws.
 */
(() => {
  if (customElements.get('press-logo-tabbed-slider')) return;

  const REDUCED = window.matchMedia ? window.matchMedia('(prefers-reduced-motion: reduce)') : { matches: false };
  const SWIPE_PX = 40; /* horizontal travel that counts as a gesture */

  class PressLogoTabbedSlider extends HTMLElement {
    connectedCallback() {
      this.stage = this.querySelector('[data-track]');
      this.tablist = this.querySelector('[data-tabs]');
      this.slides = [...this.queryAllSafe('[data-slide]')];
      this.tabs = [...this.queryAllSafe('[data-tab]')];
      if (!this.stage || !this.tablist || this.slides.length === 0 || this.slides.length !== this.tabs.length) return;

      this.ac = new AbortController();
      const { signal } = this.ac;
      const on = (el, ev, fn, opts) => el.addEventListener(ev, fn, { ...(opts || {}), signal });

      this.active = 0;
      this.count = this.slides.length;
      this.autoplayOn = this.dataset.autoplay === 'true' && !REDUCED.matches && this.count > 1;
      this.interval = Math.max(1000, parseInt(this.dataset.interval, 10) || 3000);
      this.timer = 0;
      this.hovered = false;
      this.pressed = false;
      this.visible = true;
      this.inView = true;

      /* 1. tab → quote */
      on(this.tablist, 'click', (e) => {
        const tab = e.target.closest('[data-tab]');
        if (!tab) return;
        this.goTo(Number(tab.dataset.index));
        this.restart();
      });

      /* 4. keyboard on the tablist */
      on(this.tablist, 'keydown', (e) => {
        const map = { ArrowRight: 1, ArrowLeft: -1 };
        let next = null;
        if (e.key in map) next = (this.active + map[e.key] + this.count) % this.count;
        else if (e.key === 'Home') next = 0;
        else if (e.key === 'End') next = this.count - 1;
        if (next === null) return;
        e.preventDefault();
        this.goTo(next);
        this.tabs[next].focus({ preventScroll: true });
        this.restart();
      });

      /* 2. gestures on the stage */
      this.bindGestures(on);

      /* 3. autoplay pause conditions */
      on(this, 'pointerenter', (e) => { if (e.pointerType === 'mouse') { this.hovered = true; this.stop(); } });
      on(this, 'pointerleave', (e) => { if (e.pointerType === 'mouse') { this.hovered = false; this.pressed = false; this.start(); } });
      /* Focus is read LIVE in canPlay() rather than latched here. Latching it
       * sticks: when a focused link's panel becomes `inert`, the browser's focus
       * fixup moves focus to body WITHOUT firing focusout, and a latched flag
       * would pause autoplay forever (seen after a drag that began on a link). */
      on(this, 'focusin', () => this.stop());
      on(this, 'focusout', () => setTimeout(() => this.start(), 0));
      on(document, 'visibilitychange', () => {
        this.visible = document.visibilityState !== 'hidden';
        if (this.visible) this.start(); else this.stop();
      });
      if ('IntersectionObserver' in window) {
        this.io = new IntersectionObserver((entries) => {
          this.inView = entries.some((en) => en.isIntersecting);
          if (this.inView) this.start(); else this.stop();
        }, { threshold: 0.25 });
        this.io.observe(this);
      }
      if (REDUCED.addEventListener) {
        on(REDUCED, 'change', () => {
          this.autoplayOn = this.dataset.autoplay === 'true' && !REDUCED.matches && this.count > 1;
          if (this.autoplayOn) this.start(); else this.stop();
        });
      }

      this.setActive(0);
      this.start();
    }

    disconnectedCallback() {
      this.stop();
      if (this.ac) this.ac.abort();
      if (this.io) this.io.disconnect();
    }

    /* Never throws on a partial render (the Theme Editor re-renders piecemeal). */
    queryAllSafe(sel) {
      try { return this.querySelectorAll(sel); } catch { return []; }
    }

    /* ---- state ---------------------------------------------------------- */

    goTo(index) {
      const i = (index % this.count + this.count) % this.count;
      if (i !== this.active) this.setActive(i);
    }

    focusWithin() {
      const ae = document.activeElement;
      return !!ae && ae !== document.body && this.contains(ae);
    }

    setActive(i) {
      this.active = i;
      /* Blur anything focused inside an OUTGOING panel before it goes inert, so
       * focus leaves through a real event instead of the silent fixup. */
      const ae = document.activeElement;
      if (ae && this.slides.some((s, k) => k !== i && s.contains(ae))) ae.blur();
      this.tabs.forEach((tab, k) => {
        const onTab = k === i;
        tab.classList.toggle('is-active', onTab);
        tab.setAttribute('aria-selected', onTab ? 'true' : 'false');
        if (onTab) tab.removeAttribute('tabindex'); else tab.setAttribute('tabindex', '-1');
      });
      this.slides.forEach((slide, k) => {
        const onSlide = k === i;
        slide.classList.toggle('is-active', onSlide);
        if (onSlide) slide.removeAttribute('inert'); else slide.setAttribute('inert', '');
      });
      this.revealTab(i);
      this.start(); /* re-arm if a blur above just cleared the focus gate */
    }

    /* 5. Keep the active logo inside the tab strip when the strip overflows.
     * Rect arithmetic, not offsetLeft: the tab's offsetParent is whichever
     * ancestor happens to be positioned, so offsetLeft is not list-relative.
     * Manual scrollLeft rather than scrollIntoView: the latter also scrolls the
     * PAGE vertically, which would yank the document on every autoplay tick. */
    revealTab(i) {
      const list = this.tablist;
      if (list.scrollWidth <= list.clientWidth + 1) return;
      const tab = this.tabs[i];
      const tRect = tab.getBoundingClientRect();
      const lRect = list.getBoundingClientRect();
      const target = list.scrollLeft + (tRect.left - lRect.left) - (list.clientWidth - tRect.width) / 2;
      const max = list.scrollWidth - list.clientWidth;
      const left = Math.max(0, Math.min(max, target));
      if (Math.abs(list.scrollLeft - left) > 1) {
        list.scrollTo({ left, behavior: REDUCED.matches ? 'auto' : 'smooth' });
      }
    }

    /* ---- gestures ------------------------------------------------------- */

    bindGestures(on) {
      const stage = this.stage;
      let startX = 0;
      let startY = 0;
      let tracking = false;
      let moved = false;
      let pointerId = null;

      /* A drag that begins on the article link must not FOCUS the link: mousedown
       * focus is what leaves a focused element inside a soon-inert panel. Click
       * still fires (and is swallowed after a real drag), keyboard focus is
       * untouched, and text selection is already off on the stage. */
      on(stage, 'mousedown', (e) => { if (e.button === 0) e.preventDefault(); });

      on(stage, 'pointerdown', (e) => {
        if (e.pointerType === 'mouse' && e.button !== 0) return;
        pointerId = e.pointerId;
        startX = e.clientX;
        startY = e.clientY;
        tracking = true;
        moved = false;
        this.pressed = true;
        this.stop();
      });

      on(stage, 'pointermove', (e) => {
        if (!tracking || e.pointerId !== pointerId) return;
        const dx = e.clientX - startX;
        const dy = e.clientY - startY;
        /* A mostly-vertical touch move is a page scroll — hand it back. */
        if (!moved && Math.abs(dy) > Math.abs(dx) && Math.abs(dy) > 10) { tracking = false; this.pressed = false; this.start(); return; }
        if (!moved && Math.abs(dx) < 6) return;
        if (!moved) {
          moved = true;
          stage.classList.add('is-dragging');
          try { stage.setPointerCapture(pointerId); } catch { /* not fatal */ }
        }
      });

      const end = (e) => {
        if (!tracking || (e && e.pointerId !== undefined && e.pointerId !== pointerId)) return;
        tracking = false;
        this.pressed = false;
        if (moved) {
          stage.classList.remove('is-dragging');
          try { stage.releasePointerCapture(pointerId); } catch { /* not fatal */ }
          const dx = (e && typeof e.clientX === 'number' ? e.clientX : startX) - startX;
          if (Math.abs(dx) >= SWIPE_PX) this.goTo(this.active + (dx < 0 ? 1 : -1));
          this.suppressClick = true;
        }
        this.restart();
      };
      on(stage, 'pointerup', end);
      on(stage, 'pointercancel', end);
      on(stage, 'lostpointercapture', () => { if (tracking) end(); });

      /* A click that follows a drag must not fire the link under the pointer. */
      on(stage, 'click', (e) => {
        if (!this.suppressClick) return;
        this.suppressClick = false;
        e.preventDefault();
        e.stopPropagation();
      }, { capture: true });
      on(stage, 'dragstart', (e) => e.preventDefault());
    }

    /* ---- autoplay ------------------------------------------------------- */

    canPlay() {
      return this.autoplayOn && !this.hovered && !this.focusWithin() && !this.pressed && this.visible && this.inView;
    }

    start() {
      if (this.timer || !this.canPlay()) return;
      this.timer = window.setInterval(() => {
        if (!this.canPlay()) return;
        this.goTo(this.active + 1);
      }, this.interval);
    }

    stop() {
      if (!this.timer) return;
      window.clearInterval(this.timer);
      this.timer = 0;
    }

    restart() {
      this.stop();
      this.start();
    }
  }

  customElements.define('press-logo-tabbed-slider', PressLogoTabbedSlider);
})();
